/**
 * Size drop-up in the sticky add to cart bar.
 *
 * The bar's size button opens a list of sizes upwards, as the bar sits at the
 * bottom of the screen. Choosing a size selects the same size in the main
 * size picker on the page, so the price, availability, image and button all
 * update exactly as if it had been chosen there. Choosing a size on the page
 * updates the drop-up's label in turn.
 *
 * Everything is handled from the document, so it keeps working when the bar
 * is re-rendered after a size change.
 */

const ROOT = '[data-sticky-size-select]';

const closeAll = (except) => {
  document.querySelectorAll(ROOT).forEach((root) => {
    if (root === except) return;
    root.querySelector('[data-sticky-size-toggle]')?.setAttribute('aria-expanded', 'false');
    root.querySelector('[role="listbox"]')?.setAttribute('hidden', '');
  });
};

const openList = (root) => {
  closeAll(root);
  const list = root.querySelector('[role="listbox"]');
  root.querySelector('[data-sticky-size-toggle]')?.setAttribute('aria-expanded', 'true');
  list?.removeAttribute('hidden');
  /** @type {HTMLElement | null} */
  const current = list?.querySelector('[aria-selected="true"]') || list?.querySelector('[role="option"]');
  current?.focus();
};

/** Finds the main picker's control for a size, not one in a quick add or card. */
function findMainControl(optionValueId) {
  const candidates = document.querySelectorAll(
    `input[data-option-value-id="${optionValueId}"], option[data-option-value-id="${optionValueId}"]`
  );
  return [...candidates].find(
    (el) => !el.closest('sticky-add-to-cart, .quick-add-modal, product-card, quick-add-component')
  );
}

function choose(option) {
  const root = option.closest(ROOT);
  const control = findMainControl(option.dataset.optionValueId);
  if (control instanceof HTMLInputElement) {
    if (!control.checked) control.click();
  } else if (control instanceof HTMLOptionElement) {
    const select = control.closest('select');
    if (select && select.value !== control.value) {
      select.value = control.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Reflect the choice straight away; the bar re-renders shortly after.
  if (root) {
    root.querySelectorAll('[role="option"]').forEach((item) => item.setAttribute('aria-selected', String(item === option)));
    const current = root.querySelector('[data-sticky-size-current]');
    if (current) current.textContent = option.dataset.value || '';
  }
  closeAll();
  root?.querySelector('[data-sticky-size-toggle]')?.focus();
}

if (!window.__pehrStickySize) {
  window.__pehrStickySize = true;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-sticky-size-toggle]');
    if (toggle) {
      const root = toggle.closest(ROOT);
      if (!root) return;
      if (toggle.getAttribute('aria-expanded') === 'true') closeAll();
      else openList(root);
      return;
    }

    const option = event.target.closest(`${ROOT} [role="option"]`);
    if (option instanceof HTMLElement) {
      choose(option);
      return;
    }

    if (!event.target.closest(ROOT)) closeAll();
  });

  document.addEventListener('keydown', (event) => {
    const root = document.activeElement?.closest?.(ROOT);
    if (!root) return;
    const options = [...root.querySelectorAll('[role="option"]')];
    const index = options.indexOf(/** @type {Element} */ (document.activeElement));

    if (event.key === 'Escape') {
      closeAll();
      root.querySelector('[data-sticky-size-toggle]')?.focus();
    } else if (index > -1 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const next = options[(index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length];
      /** @type {HTMLElement} */ (next).focus();
    } else if (index > -1 && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      choose(/** @type {HTMLElement} */ (options[index]));
    }
  });

  // A size chosen in the main picker updates the drop-up's label.
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (target.closest(`${ROOT}, .quick-add-modal, product-card, quick-add-component`)) return;
    const valueId =
      target instanceof HTMLSelectElement
        ? target.selectedOptions[0]?.dataset.optionValueId
        : target.dataset.optionValueId;
    if (!valueId) return;
    document.querySelectorAll(`${ROOT} [role="option"]`).forEach((item) => {
      const match = item.getAttribute('data-option-value-id') === valueId;
      item.setAttribute('aria-selected', String(match));
      if (match) {
        const current = item.closest(ROOT)?.querySelector('[data-sticky-size-current]');
        if (current) current.textContent = item.getAttribute('data-value') || '';
      }
    });
  });
}
